import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ChatbotProps } from '../types';

type HostMeta = {
  active: boolean;
  overrides?: Partial<ChatbotProps> | null;
  priority?: number; // higher wins
  lastActivatedAt?: number;
};

type RegisterOptions = {
  active?: boolean;
  overrides?: Partial<ChatbotProps> | null;
  priority?: number;
};

interface GlobalChatContextValue {
  // Visibility
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Overrides
  runtimeOverrides: Partial<ChatbotProps> | null;
  setOverrides: (overrides?: Partial<ChatbotProps> | null) => void;
  clearOverrides: () => void;
  openWithOverrides: (overrides: Partial<ChatbotProps>) => void;

  // Host management
  hostEl: HTMLElement | null;
  hostOverrides: Partial<ChatbotProps> | null;
  registerHost: (el: HTMLElement, options?: RegisterOptions) => () => void;
  setHostActive: (el: HTMLElement, active: boolean) => void;
  updateHostOverrides: (el: HTMLElement, overrides?: Partial<ChatbotProps> | null) => void;
}

const GlobalChatContext = createContext<GlobalChatContextValue | null>(null);

export function useGlobalChat(): GlobalChatContextValue {
  const ctx = useContext(GlobalChatContext);
  if (!ctx) {
    throw new Error('useGlobalChat must be used within a GlobalChatProvider');
  }
  return ctx;
}

function selectActiveHost(hosts: Map<HTMLElement, HostMeta>): { el: HTMLElement | null; meta: HostMeta | null } {
  const activeEntries: Array<[HTMLElement, HostMeta]> = [];
  hosts.forEach((meta, el) => {
    if (meta.active) activeEntries.push([el, meta]);
  });
  if (activeEntries.length === 0) return { el: null, meta: null };

  activeEntries.sort((a, b) => {
    const pa = a[1].priority ?? 0;
    const pb = b[1].priority ?? 0;
    if (pb !== pa) return pb - pa; // higher priority first
    const ta = a[1].lastActivatedAt ?? 0;
    const tb = b[1].lastActivatedAt ?? 0;
    return tb - ta; // most recently activated first
  });

  const [el, meta] = activeEntries[0];
  return { el, meta };
}

export function GlobalChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('dopo:chat:isOpen') === '1';
    } catch {
      return false;
    }
  });

  const hostsRef = useRef<Map<HTMLElement, HostMeta>>(new Map());
  const [hostEl, setHostEl] = useState<HTMLElement | null>(null);
  const [hostOverrides, setHostOverrides] = useState<Partial<ChatbotProps> | null>(null);

  const [runtimeOverrides, setRuntimeOverrides] = useState<Partial<ChatbotProps> | null>(null);

  // Hysteresis for host switching to prevent bounce
  const hysteresisMs = 250;
  const pendingSwitchRef = useRef<{ target: HTMLElement | null; timer: number | null }>({
    target: null,
    timer: null,
  });

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    // Clear runtime overrides on close to avoid carry-over surprises
    setRuntimeOverrides(null);
  }, []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);

  const setOverrides = useCallback((overrides?: Partial<ChatbotProps> | null) => {
    setRuntimeOverrides(overrides ?? null);
  }, []);
  const clearOverrides = useCallback(() => {
    setRuntimeOverrides(null);
  }, []);
  const openWithOverrides = useCallback((overrides: Partial<ChatbotProps>) => {
    setRuntimeOverrides(overrides || {});
    setIsOpen(true);
  }, []);

  const pruneDisconnectedHosts = useCallback(() => {
    // Drop any hosts that are no longer connected to the DOM
    hostsRef.current.forEach((_meta, el) => {
      if (typeof document !== 'undefined' && !document.contains(el)) {
        hostsRef.current.delete(el);
      }
    });
  }, []);

  const commitActiveHost = useCallback((el: HTMLElement | null, meta: HostMeta | null) => {
    setHostEl(prevEl => {
      if (prevEl === el) {
        // If same host, just update overrides if changed
        if (el && meta) setHostOverrides(meta.overrides ?? null);
        if (!el) setHostOverrides(null);
        return prevEl;
      }
      // Switch host
      setHostOverrides(meta?.overrides ?? null);
      return el;
    });
  }, []);

  const recomputeActiveHost = useCallback((withHysteresis: boolean = true) => {
    pruneDisconnectedHosts();
    const { el: candidateEl, meta: candidateMeta } = selectActiveHost(hostsRef.current);

    // If the candidate is the same as current, cancel any pending switch and update overrides
    if (candidateEl === hostEl) {
      // Cancel pending switch to a different host
      if (pendingSwitchRef.current.timer) {
        clearTimeout(pendingSwitchRef.current.timer);
        pendingSwitchRef.current.timer = null;
        pendingSwitchRef.current.target = null;
      }
      if (candidateEl) {
        setHostOverrides(candidateMeta?.overrides ?? null);
      } else {
        setHostOverrides(null);
      }
      return;
    }

    if (!withHysteresis) {
      commitActiveHost(candidateEl, candidateMeta);
      return;
    }

    // Start or refresh hysteresis timer for the new candidate
    const currentPending = pendingSwitchRef.current;
    if (currentPending.timer && currentPending.target === candidateEl) {
      // Already pending switch to this candidate; let the timer complete
      return;
    }
    if (currentPending.timer) {
      clearTimeout(currentPending.timer);
      currentPending.timer = null;
      currentPending.target = null;
    }

    pendingSwitchRef.current.target = candidateEl;
    pendingSwitchRef.current.timer = window.setTimeout(() => {
      pendingSwitchRef.current.timer = null;
      pendingSwitchRef.current.target = null;

      // Re-evaluate before committing, in case things changed
      const { el: freshEl, meta: freshMeta } = selectActiveHost(hostsRef.current);
      commitActiveHost(freshEl, freshMeta);
    }, hysteresisMs) as unknown as number;
  }, [commitActiveHost, hostEl, pruneDisconnectedHosts]);

  const registerHost = useCallback((el: HTMLElement, options?: RegisterOptions) => {
    const current = hostsRef.current.get(el);
    const meta: HostMeta = {
      active: Boolean(options?.active),
      overrides: options?.overrides ?? null,
      priority: options?.priority ?? 0,
      lastActivatedAt: options?.active ? Date.now() : (current?.lastActivatedAt ?? 0),
    };
    hostsRef.current.set(el, meta);
    // Recompute with hysteresis to avoid thrash if multiple register quickly
    recomputeActiveHost(true);

    return () => {
      hostsRef.current.delete(el);
      if (hostEl === el) {
        recomputeActiveHost(false); // immediate recompute if removing the current host
      }
    };
  }, [hostEl, recomputeActiveHost]);

  const setHostActive = useCallback((el: HTMLElement, active: boolean) => {
    const meta = hostsRef.current.get(el);
    if (!meta) return;
    hostsRef.current.set(el, {
      ...meta,
      active,
      lastActivatedAt: active ? Date.now() : meta.lastActivatedAt,
    });
    recomputeActiveHost(true);
  }, [recomputeActiveHost]);

  const updateHostOverrides = useCallback((el: HTMLElement, overrides?: Partial<ChatbotProps> | null) => {
    const meta = hostsRef.current.get(el);
    if (!meta) return;
    const nextMeta = { ...meta, overrides: overrides ?? null };
    hostsRef.current.set(el, nextMeta);
    // Only update state if this host is the selected one
    if (hostEl === el) {
      setHostOverrides(overrides ?? null);
    }
  }, [hostEl]);

  const value = useMemo<GlobalChatContextValue>(() => ({
    isOpen,
    open,
    close,
    toggle,
    runtimeOverrides,
    setOverrides,
    clearOverrides,
    openWithOverrides,
    hostEl,
    hostOverrides,
    registerHost,
    setHostActive,
    updateHostOverrides,
  }), [
    isOpen,
    open,
    close,
    toggle,
    runtimeOverrides,
    setOverrides,
    clearOverrides,
    openWithOverrides,
    hostEl,
    hostOverrides,
    registerHost,
    setHostActive,
    updateHostOverrides,
  ]);

  // Persist isOpen on change (also set at container level so either path survives SSR)
  React.useEffect(() => {
    try {
      sessionStorage.setItem('dopo:chat:isOpen', isOpen ? '1' : '0');
    } catch {}
  }, [isOpen]);

  return (
    <GlobalChatContext.Provider value={value}>
      {children}
    </GlobalChatContext.Provider>
  );
}

export default GlobalChatProvider;
